/**
 * Parser CSV minimale per l'import utenti dalla pagina admin.
 *
 * Il CSV è interpretato lato client e trasformato in righe
 * `{ email, displayName?, role? }` da inviare a `POST /api/users/import`.
 * L'email è la chiave: il backend salta le email già presenti.
 *
 * Formato atteso:
 *  - prima riga di intestazione con i nomi delle colonne (case‑insensitive);
 *  - colonna `email` obbligatoria;
 *  - colonne opzionali `displayName` (alias: `name`, `nome`) e `role`
 *    (alias: `ruolo`).
 *  - separatore virgola `,`; sono supportati i campi tra virgolette doppie
 *    con virgolette raddoppiate per l'escape (`"a""b"` → `a"b`).
 *
 * Non è un parser CSV completo (niente separatori alternativi), ma copre i
 * casi tipici di export da fogli di calcolo.
 */
import type { ImportUserRow, Role } from '../shared/dto';

/** Errore sollevato quando il contenuto CSV non è utilizzabile. */
export class CsvParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CsvParseError';
  }
}

const VALID_ROLES: readonly Role[] = ['admin', 'manager', 'reviewer', 'employer'];

/** Alias di intestazione accettati per ciascun campo (normalizzati lowercase). */
const HEADER_ALIASES: Record<'email' | 'displayName' | 'role', readonly string[]> = {
  email: ['email', 'e-mail', 'mail'],
  displayName: ['displayname', 'display name', 'name', 'nome', 'nome completo'],
  role: ['role', 'ruolo'],
};

/**
 * Scompone una singola riga CSV in campi, gestendo le virgolette doppie.
 */
function splitCsvLine(line: string): string[] {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i += 1; // salta la virgoletta di escape
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      fields.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  fields.push(current);
  return fields;
}

/** Individua l'indice della colonna corrispondente a un campo, o -1. */
function findColumn(header: string[], field: keyof typeof HEADER_ALIASES): number {
  const aliases = HEADER_ALIASES[field];
  return header.findIndex((h) => aliases.includes(h.trim().toLowerCase()));
}

/**
 * Interpreta il testo CSV in un elenco di righe utente pronte per l'import.
 *
 * @throws {CsvParseError} se il file è vuoto, manca la colonna `email`, non
 *   contiene righe dati, o un `role` non è valido.
 */
export function parseUsersCsv(text: string): ImportUserRow[] {
  // Normalizza i fine riga e scarta le righe vuote.
  const lines = text
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .filter((l) => l.trim() !== '');

  if (lines.length === 0) {
    throw new CsvParseError('Il file CSV è vuoto.');
  }

  const header = splitCsvLine(lines[0]);
  const emailIdx = findColumn(header, 'email');
  if (emailIdx === -1) {
    throw new CsvParseError('Colonna "email" mancante nell\'intestazione del CSV.');
  }
  const nameIdx = findColumn(header, 'displayName');
  const roleIdx = findColumn(header, 'role');

  const rows: ImportUserRow[] = [];
  for (let i = 1; i < lines.length; i += 1) {
    const cells = splitCsvLine(lines[i]);
    const email = (cells[emailIdx] ?? '').trim();
    if (email === '') continue; // salta righe senza email

    const row: ImportUserRow = { email };

    if (nameIdx !== -1) {
      const displayName = (cells[nameIdx] ?? '').trim();
      if (displayName !== '') row.displayName = displayName;
    }

    if (roleIdx !== -1) {
      const raw = (cells[roleIdx] ?? '').trim().toLowerCase();
      if (raw !== '') {
        if (!VALID_ROLES.includes(raw as Role)) {
          throw new CsvParseError(
            `Ruolo non valido "${raw}" alla riga ${i + 1}. Valori ammessi: ${VALID_ROLES.join(', ')}.`,
          );
        }
        row.role = raw as Role;
      }
    }

    rows.push(row);
  }

  if (rows.length === 0) {
    throw new CsvParseError('Nessuna riga utente valida nel CSV.');
  }

  return rows;
}
